import {MNullable} from '../Toolkit/Utils';
import * as Yup from 'yup';
import {Note} from './NoteTypes';

export class NoticeHeader {

    public static readonly OdinIdMaxLength = 30;
    public static readonly RequestorMaxLength = 255;
    public static readonly L2IdMaxLength = 20;

    public id: number = 0;
    public odinId: string = '';
    public l2Id?: string = '';
    public safeDigId: number = -1;
    public parentId: number = -1;
    public requestedDate: MNullable<Date> = null;
    public project: number = 0;
}

export class NoticeDetail {

    public static readonly OdinIdMaxLength = 30;
    public static readonly Address1MaxLength = 50;
    public static readonly Address2MaxLength = 50;
    public static readonly Address3MaxLength = 50;
    public static readonly CountyMaxLength = 50;
    public static readonly WorkDescriptionMaxLength = 500;
    public static readonly RequestorMaxLength = 255;
    public static readonly PermitNumberMaxLength = 30;
    public static readonly HoursOfWorkMaxLength = 20;
    public static readonly UsrnMaxLength = 20;
    public static readonly CancelNoteMaxLength = 100;

    public id: number = 0;
    public headerId: number = -1;
    public requestedDate: MNullable<Date> = null;
    public address1: string = ''
    public address2: string = '';
    public address3: string = '';
    public county: string = '';
    public easting: number = 0;
    public northing: number = 0;
    public easting2: number = 0;
    public northing2: number = 0;
    public worksLocations: number[] = [];
    public workDescription: string = '';
    public requestor: string = '';
    public phaseType: number = 0;
    public cancelReason: number = 0;
    public cancelNote: string = '';
    public startDate: MNullable<Date> = null;
    public endDate: MNullable<Date> = null;
    public tmType: number[] = [];
    public fwClosureType: number = 0;
    public permitNumber: string = '';
    public submissionDate: MNullable<Date> = null;
    public permitStatus: number = 0;
    public trafficSensitiveHours: number = -1;
    public hoursOfWork: string = '';
    public excavationRequired: boolean = false;
    public noticeStatus: number = 0;
    public usrn?: string = undefined;
    public permitType: number = 0;
    public roadType: number = 0;
    public project: number = 0;
    public subResource: number = -1;
}

export class CreateNoticeValidator {
    public static readonly odinId = Yup.string().label("OdinId").max(NoticeHeader.OdinIdMaxLength).required();
    public static readonly requestedDate = Yup.date().required();
    public static readonly address1 = Yup.string().label("Address1").max(NoticeDetail.Address1MaxLength).required();
    public static readonly address2 = Yup.string().label("Address2").max(NoticeDetail.Address2MaxLength);
    public static readonly address3 = Yup.string().label("Address3").max(NoticeDetail.Address3MaxLength);
    public static readonly county = Yup.string().label("Address1").max(NoticeDetail.CountyMaxLength).required();
    public static readonly easting = Yup.number().nullable().required("Easting value is required");
    public static readonly northing = Yup.number().nullable().required("Northing value is required");
    public static readonly easting2 = Yup.number().nullable().optional();
    public static readonly northing2 = Yup.number().nullable().optional();
    public static readonly worksLocations = Yup.array().test({
        message: "Works Location is Required",
        test: arr => arr === undefined ? false : arr!.length > 0
    });
    public static readonly tmType = Yup.array().test({
        message: "TM Type is required",
        test: arr => arr === undefined ? false : arr!.length > 0
    });
    public static readonly workDescription = Yup.string().label("WorkDescription").max(NoticeDetail.WorkDescriptionMaxLength).required();
    public static readonly requestor = Yup.string().label("Requestor").max(NoticeDetail.RequestorMaxLength).required();
    public static readonly startDate = Yup.date().nullable().required("Start date is required");
    public static readonly endDate = Yup.date().nullable().required("End date is required");
    public static readonly fwClosureType = Yup.number().required().min(0, "Footway Closure type is required");
    public static readonly trafficSensitiveHours = Yup.number().min(0, "Traffic Sensitive Hours is required");
    public static readonly hoursOfWork = Yup.string().max(NoticeDetail.HoursOfWorkMaxLength).required();
    public static readonly excavationRequired = Yup.number().min(0, "Excavation is required");
    public static readonly permitType = Yup.number().min(0, "Permit Type is required");
    public static readonly l2id = Yup.string().label("L2ID").max(NoticeHeader.L2IdMaxLength);
    public static readonly project = Yup.number().min(0, "Project is required");
}

export class EditNoticeValidator {
    public static readonly odinId = Yup.string().label("OdinId").max(NoticeHeader.OdinIdMaxLength).required();
    public static readonly requestedDate = Yup.date().required();
    public static readonly address1 = Yup.string().label("Address1").max(NoticeDetail.Address1MaxLength).required();
    public static readonly address2 = Yup.string().label("Address2").max(NoticeDetail.Address2MaxLength);
    public static readonly address3 = Yup.string().label("Address3").max(NoticeDetail.Address3MaxLength);
    public static readonly county = Yup.string().label("Address1").max(NoticeDetail.CountyMaxLength).required();
    public static readonly easting = Yup.number().nullable().required("Easting value is required");
    public static readonly northing = Yup.number().nullable().required("Northing value is required");
    public static readonly easting2 = Yup.number().nullable().optional();
    public static readonly northing2 = Yup.number().nullable().optional();
    public static readonly worksLocations = Yup.array().test({
        message: "Works Location is Required",
        test: arr => arr === undefined ? false : arr!.length > 0
    });
    public static readonly tmType = Yup.array().test({
        message: "TM Type is required",
        test: arr => arr === undefined ? false : arr!.length > 0
    });
    public static readonly workDescription = Yup.string().label("WorkDescription").max(NoticeDetail.WorkDescriptionMaxLength).required();
    public static readonly requestor = Yup.string().label("Requestor").max(NoticeDetail.RequestorMaxLength).required();
    public static readonly cancelReason = Yup.number().test(
        'cancelReason',
        'Cancel Reason is required for cancelled job',
        (value, ctx) => {
            if(ctx.parent.phaseType === 3 && value === -1)  //Job is cancelled but no reason given
            {
                return false;
            }
            return true;
        }
    );
    public static readonly cancelNote = Yup.string().test(
        'cancelNote',
        'Note is required for Cancellation Reason',
        (value, ctx) => {
            const noteRequired = [9,10,12,16];
            if(ctx.parent.phaseType === 3 && noteRequired.includes(ctx.parent.cancelReason) && (value === undefined || value === ''))  //Job is cancelled but no note given
            {
                return false;
            }
            return true;
        }
    );
    public static readonly startDate = Yup.date().nullable().required("Start date is required");
    public static readonly endDate = Yup.date().nullable().required("End date is required");
    public static readonly fwClosureType = Yup.number().required().min(0, "Footway Closure type is required");
    public static readonly trafficSensitiveHours = Yup.number().min(0, "Traffic Sensitive Hours is required");
    public static readonly permitNumber = Yup.string().label("PermitNumber").max(NoticeDetail.PermitNumberMaxLength).optional();
    public static readonly hoursOfWork = Yup.string().max(NoticeDetail.HoursOfWorkMaxLength).required();
    public static readonly usrn = Yup.string().nullable().label("USRN").max(NoticeDetail.UsrnMaxLength);
    public static readonly permitType = Yup.number().min(0, "Permit Type is required");
    public static readonly l2id = Yup.string().nullable().label("L2ID").max(NoticeHeader.L2IdMaxLength);
    public static readonly roadType = Yup.number().min(0, "Road Type is required");
    public static readonly project = Yup.number().min(0, "Project is required");
}

export class NoticeHeaderReply {
    public noticeHeader: MNullable<NoticeHeader> = null;
}

export class NoticeDetailReply {
    public notice: MNullable<NoticeDetail> = null;
    public notes: Note[] = [];
}

export class NoticeDetailListReply {
    public noticeDetails: NoticeDetail[] = [];
}

export class Notice {
    public header: MNullable<NoticeHeader> = null;
    public detail: MNullable<NoticeDetail> = null;
    public permitFileState: number = 0;
    public tmFileState: number = 0;
}

export class NoticeReply {
    public header: MNullable<NoticeHeader> = null;
    public detail: MNullable<NoticeDetail> = null;
    public notes: Note[] = [];
    public permitFileState: number = 0;
    public tmFileState: number = 0;
}

export class NoticeReplyMulti {
    public notices: NoticeReply[] = [];
}

